import { useCallback, useRef, useState } from 'react'
import { isAxiosError } from 'axios'
import { logHeardError } from '@/utils/errorHelpers'
import { useAppDispatch } from '@/utils/typeHelpers'
import { useAnalyticsTrack } from '@/features/amplitude'
import {
  CARD_UPDATE_EVENTS,
  type CardUpdateSurface,
} from './card-update-analytics.constants'
import { createSetupIntent, finalizeSetupIntent } from './paymentMethodActions'
import { getStripe } from './stripeClient'
import type {
  CardSummary,
  PaymentMethodUpdateError,
  PaymentMethodUpdateStatus,
  SetupIntentSession,
} from './payment-method.types'

// The server refused this SetupIntent (foreign, missing, or not succeeded): retrying the same entry cannot help.
const FINALIZE_REFUSED_STATUSES = new Set([403, 404, 409])

interface SetupIntentLike {
  id: string
  status: string
}

/** The slice of a stripe-js `confirmSetup` result the hook reads. */
export interface SetupOutcome {
  error?: {
    type: string
    code?: string
    decline_code?: string
    message?: string
    setup_intent?: SetupIntentLike
  }
  setupIntent?: SetupIntentLike
}

interface AddressValueLike {
  complete: boolean
  value: {
    name: string
    address: {
      line1: string
      line2: string | null
      city: string
      state: string
      postal_code: string
      country: string
    }
  }
}

/** The slice of `StripeElements` the hook calls. Method shorthand keeps the real type assignable. */
export interface ElementsLike {
  getElement(type: 'address'): { getValue(): Promise<AddressValueLike> } | null
}

/** The slice of `Stripe` the hook calls. Generic keeps the real overloaded `confirmSetup` assignable. */
export interface SetupConfirmer {
  confirmSetup<E extends ElementsLike = ElementsLike>(options: {
    elements: E
    redirect: 'if_required'
  }): Promise<SetupOutcome>
}

export interface PaymentMethodSaveContext {
  stripe: SetupConfirmer
  elements: ElementsLike
}

export interface PaymentMethodUpdate {
  status: PaymentMethodUpdateStatus
  error: PaymentMethodUpdateError | null
  session: SetupIntentSession | null
  open: () => void
  cancel: () => void
  save: (context: PaymentMethodSaveContext) => Promise<void>
}

export const usePaymentMethodUpdate = ({
  surface,
  onSaved,
}: {
  surface: CardUpdateSurface
  onSaved?: (card: CardSummary | null) => void
}): PaymentMethodUpdate => {
  const dispatch = useAppDispatch()
  const track = useAnalyticsTrack()
  const [status, setStatus] = useState<PaymentMethodUpdateStatus>('idle')
  const [error, setError] = useState<PaymentMethodUpdateError | null>(null)
  const [session, setSession] = useState<SetupIntentSession | null>(null)
  const savingRef = useRef(false)
  const loadGenerationRef = useRef(0)
  const isLoadingRef = useRef(false)

  const trackFailed = useCallback(
    (reason: PaymentMethodUpdateError, code: string | null) =>
      track(CARD_UPDATE_EVENTS.Failed, {
        surface,
        reason,
        declineCode: code,
      }),
    [track, surface]
  )

  const open = useCallback(() => {
    if (isLoadingRef.current) return
    isLoadingRef.current = true
    const generation = ++loadGenerationRef.current
    setStatus('loading')
    setError(null)
    setSession(null)
    const load = async (): Promise<void> => {
      try {
        const created = await createSetupIntent()(dispatch)
        const stripe = await getStripe(created.publishableKey)
        if (!stripe) throw new Error('Stripe.js did not load')
        if (generation !== loadGenerationRef.current) return
        setSession(created)
        setStatus('ready')
        track(CARD_UPDATE_EVENTS.Viewed, { surface })
      } catch (err) {
        if (!isAxiosError(err)) logHeardError(err)
        if (generation !== loadGenerationRef.current) return
        setSession(null)
        setError('load_failed')
        setStatus('ready')
        trackFailed('load_failed', null)
      } finally {
        if (generation === loadGenerationRef.current)
          isLoadingRef.current = false
      }
    }
    void load()
  }, [dispatch, track, surface, trackFailed])

  const cancel = useCallback(() => {
    if (savingRef.current) return
    loadGenerationRef.current += 1
    isLoadingRef.current = false
    setStatus('idle')
    setError(null)
    setSession(null)
  }, [])

  const decline = useCallback(
    (reason: PaymentMethodUpdateError, code: string | null = null) => {
      setError(reason)
      setStatus('declined')
      trackFailed(reason, code)
    },
    [trackFailed]
  )

  const finalize = useCallback(
    async (setupIntentId: string): Promise<void> => {
      try {
        const saved = await finalizeSetupIntent(setupIntentId)(dispatch)
        setSession(null)
        setStatus('saved')
        track(CARD_UPDATE_EVENTS.Succeeded, { surface })
        onSaved?.(saved)
      } catch (err) {
        const httpStatus = isAxiosError(err) ? err.response?.status : undefined
        if (
          httpStatus !== undefined &&
          FINALIZE_REFUSED_STATUSES.has(httpStatus)
        ) {
          // A succeeded SetupIntent cannot be confirmed again, so a retry needs a fresh one.
          setSession(null)
          decline('generic')
          return
        }
        // Stripe holds the card; the webhook backstop applies it, so this is not a decline.
        setError('finalize_failed')
        setStatus('ready')
        trackFailed('finalize_failed', null)
      }
    },
    [dispatch, onSaved, decline, track, surface, trackFailed]
  )

  const save = useCallback<PaymentMethodUpdate['save']>(
    async ({ stripe, elements }) => {
      if (savingRef.current) return
      savingRef.current = true
      setStatus('saving')
      setError(null)
      try {
        // getValue validates, so Stripe already marks the missing fields.
        const address = await elements.getElement('address')?.getValue()
        if (address && !address.complete) {
          setStatus('ready')
          return
        }
        const result = await stripe.confirmSetup({
          elements,
          redirect: 'if_required',
        })
        const isValidationError = result.error?.type === 'validation_error'
        if (!isValidationError) {
          track(CARD_UPDATE_EVENTS.Submitted, { surface })
        }
        if (result.error) {
          // confirmSetup can report an error for a SetupIntent that still succeeded.
          const carried = result.error.setup_intent
          if (carried?.status === 'succeeded') {
            await finalize(carried.id)
          } else if (isValidationError) {
            setStatus('ready')
          } else if (
            result.error.code === 'setup_intent_authentication_failure'
          ) {
            decline('authentication')
          } else if (result.error.type === 'card_error') {
            decline(
              'declined',
              result.error.decline_code ?? result.error.code ?? null
            )
          } else {
            decline('generic')
          }
          return
        }
        if (result.setupIntent?.status !== 'succeeded') {
          decline('generic')
          return
        }
        await finalize(result.setupIntent.id)
      } catch {
        decline('generic')
      } finally {
        savingRef.current = false
      }
    },
    [decline, finalize, track, surface]
  )

  return { status, error, session, open, cancel, save }
}
